import React, { useEffect, useState, useCallback } from "react";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, Database, Wand2, CheckCircle2, AlertTriangle, RefreshCw } from "lucide-react";

const fmt = (n) => Number(n || 0).toLocaleString("pt-BR");

export default function AdminPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [userStats, setUserStats] = useState(null);
  const [visitorStats, setVisitorStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [gen, setGen] = useState(1000);
  const [generating, setGenerating] = useState(false);

    const load = useCallback(() => {
    setLoading(true);

    api.get("/admin/questions/stats")
      .then((r) => setStats(r.data))
      .catch((e) => toast.error(formatError(e.response?.data?.detail)))
      .finally(() => setLoading(false));

    api.get("/admin/users/stats")
      .then((r) => setUserStats(r.data))
      .catch(() => setUserStats(null));
    api.get("/admin/analytics/visitors")
      .then((r) => setVisitorStats(r.data))
      .catch(() => setVisitorStats(null));
  }, []);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/admin/questions/generate", { count: Number(gen) });
      toast.success(`Geradas: ${data.inseridas} inseridas · ${data.duplicadas} duplicadas ignoradas · total ${data.total_now}`);
      load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally {
      setGenerating(false);
    }
  };

  if (user && user.role !== "admin") {
    return <div className="mx-auto max-w-3xl px-4 py-16 text-center text-slate-400" data-testid="admin-forbidden">Acesso restrito ao administrador.</div>;
  }

  return (
    <div data-testid="admin-page" className="mx-auto max-w-5xl space-y-6 px-4 py-6 sm:px-6 sm:py-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-extrabold text-white">Painel Admin — Banco de Questões</h1>
          <p className="text-sm text-slate-400">Estatísticas reais do MongoDB e geração de questões autorais.</p>
        </div>
        <button onClick={load} data-testid="admin-refresh" className="flex items-center gap-2 rounded-lg bg-slate-800 px-3 py-2 text-sm font-semibold text-slate-200 hover:bg-slate-700">
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Atualizar
        </button>
      </div>

            {userStats && (
        <div className="space-y-4">
          <div className="rounded-2xl border border-slate-800 bg-slate-900 p-5">
            <p className="text-xs font-bold uppercase tracking-wide text-slate-400">
              Usuários cadastrados
            </p>
            <p className="mt-2 text-3xl font-extrabold text-emerald-400">
              {fmt(userStats.total_users)}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            {[
              ["Usuários gratuitos", userStats.free_users],
              ["Premium ativos", userStats.premium_active],
              ["Em teste gratuito", userStats.premium_trial],
              ["Premium vitalícios", userStats.premium_lifetime],
              ["Assinaturas vencidas", userStats.expired_subscriptions],
            ].map(([label, value]) => (
              <div
                key={label}
                className="rounded-2xl border border-slate-800 bg-slate-900 p-5"
              >
                <p className="text-xs font-bold uppercase tracking-wide text-slate-400">
                  {label}
                </p>
                <p className="mt-2 text-3xl font-extrabold text-emerald-400">
                  {fmt(value ?? 0)}
                </p>
              </div>
            ))}
          </div>
                </div>
      )}

      {visitorStats && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          {[
            ["Visitantes hoje", visitorStats.visitors_today],
            ["Últimos 7 dias", visitorStats.visitors_7_days],
            ["Últimos 30 dias", visitorStats.visitors_30_days],
            ["Ativos agora (30 min)", visitorStats.active_last_30_minutes],
          ].map(([label, value]) => (
            <div
              key={label}
              className="rounded-2xl border border-slate-800 bg-slate-900 p-5"
            >
              <p className="text-xs font-bold uppercase tracking-wide text-slate-400">
                {label}
              </p>
              <p className="mt-2 text-3xl font-extrabold text-emerald-400">
                {fmt(value)}
              </p>
            </div>
          ))}
        </div>
      )}
      {visitorStats?.daily_visitors?.length > 0 && (
        <div className="rounded-2xl border border-slate-800 bg-slate-900 p-5">
          <h2 className="mb-2 text-lg font-bold text-white">
            Evolução diária de visitantes
          </h2>
          <p className="mb-5 text-sm text-slate-400">
            Dados reais do Google Analytics desde 07/10/2026
          </p>

          <div className="flex h-56 items-end gap-2 overflow-x-auto border-b border-slate-700 pb-2">
            {visitorStats.daily_visitors.map((day) => {
              const max = Math.max(
                1,
                ...visitorStats.daily_visitors.map((d) => d.visitors)
              );

              const height = (day.visitors / max) * 100;

              return (
                <div
                  key={day.date}
                  className="flex h-full min-w-12 flex-1 flex-col items-center justify-end gap-2"
                >
                  <span className="text-xs font-bold text-emerald-400">
                    {fmt(day.visitors)}
                  </span>

                  <div
                    className="w-8 rounded-t-md bg-emerald-500"
                    style={{
                      height: `${height}%`,
                      minHeight: day.visitors > 0 ? "4px" : "0px",
                    }}
                  />

                  <span className="text-xs text-slate-400">
                    {day.date.slice(6, 8)}/{day.date.slice(4, 6)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {stats && (
        <>
          <div data-testid="admin-meta" className={`flex items-center gap-3 rounded-2xl border p-5 ${stats.meta_atingida ? "border-emerald-500/40 bg-emerald-500/5" : "border-amber-500/40 bg-amber-500/5"}`}>
            {stats.meta_atingida ? <CheckCircle2 className="h-6 w-6 text-emerald-400" /> : <AlertTriangle className="h-6 w-6 text-amber-400" />}
            <div>
              <p className={`font-bold ${stats.meta_atingida ? "text-emerald-300" : "text-amber-300"}`}>{stats.meta}</p>
              {!stats.meta_atingida && (
                <p className="text-xs text-slate-400">Total real: <b data-testid="admin-total">{fmt(stats.ativas)}</b> · Faltam <b>{fmt(stats.faltantes_para_100k)}</b> para 100.000</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            {[
              { l: "Total ativas", v: stats.ativas, t: "admin-total" },
              { l: "Autorais", v: stats.autorais },
              { l: "Reais", v: stats.reais },
              { l: "Importadas", v: stats.importadas },
              { l: "Duplicadas", v: stats.duplicadas },
              { l: "Inválidas", v: stats.invalidas },
            ].map((c) => (
              <div key={c.l} className="rounded-2xl border border-slate-800 bg-slate-900 p-4">
                <p className="text-xs uppercase tracking-wide text-slate-500">{c.l}</p>
                <p data-testid={c.t} className="font-mono-stats text-2xl font-bold text-white">{fmt(c.v)}</p>
              </div>
            ))}
          </div>

          <div className="rounded-2xl border border-slate-800 bg-slate-900 p-5">
            <div className="mb-4 flex items-center gap-2 text-emerald-400"><Wand2 className="h-5 w-5" /><span className="font-bold">Gerar questões autorais (origem=AUTORAL, tipo=SIMULADA)</span></div>
            <div className="flex flex-wrap items-center gap-3">
              <input type="number" min={1} max={20000} value={gen} onChange={(e) => setGen(e.target.value)}
                data-testid="admin-gen-count" className="w-32 rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-white" />
              <button onClick={generate} disabled={generating} data-testid="admin-generate-btn"
                className="flex items-center gap-2 rounded-lg bg-emerald-500 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-600 disabled:opacity-60">
                {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Database className="h-4 w-4" />} Gerar e salvar
              </button>
              <span className="text-xs text-slate-500">Deduplicação automática por hash — duplicadas não contam.</span>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <StatList title="Por banca" rows={stats.por_banca} testid="admin-por-banca" />
            <StatList title="Por disciplina" rows={stats.por_disciplina} testid="admin-por-disciplina" />
            <StatList title="Por concurso/órgão" rows={stats.por_concurso} testid="admin-por-concurso" />
            <StatList title="Por dificuldade" rows={stats.por_dificuldade} testid="admin-por-dificuldade" />
          </div>
        </>
      )}
    </div>
  );
}

const StatList = ({ title, rows, testid }) => (
  <div data-testid={testid} className="rounded-2xl border border-slate-800 bg-slate-900 p-5">
    <p className="mb-3 text-xs font-bold uppercase tracking-wide text-slate-400">{title}</p>
    <div className="space-y-1.5 max-h-64 overflow-y-auto">
      {(rows || []).map((r) => (
        <div key={String(r._id)} className="flex items-center justify-between text-sm">
          <span className="text-slate-300">{r._id === null ? "Não classificado" : String(r._id)}</span>
          <span className="font-mono-stats text-emerald-400">{fmt(r.count)}</span>
        </div>
      ))}
    </div>
  </div>
);
